// Shrinkage = the unaccounted-for mass between what was tipped vs what was
// packed + rejected + wasted. Converting it to a Rand value makes the
// operational signal land harder than kilograms alone — a 50 kg variance
// feels mild but "R 750 lost on this run" pulls the right kind of attention.

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useActiveTenant } from '@/lib/useActiveTenant'

export interface RunMass {
  tipped_net_weight_kg: number
  packed_weight_kg:     number
  reject_weight_kg:     number
  waste_weight_kg:      number
  variety:              string | null
}

export interface ShrinkageBreakdown {
  variance_kg: number     // tipped − packed − reject − waste (can be negative)
  shrinkage_kg: number    // max(0, variance_kg) — the part we treat as "lost"
  fruit_type:  string     // resolved category
  rate:        number     // R/kg used
  value_zar:   number     // shrinkage_kg × rate
  priced:      boolean    // false when no R/kg rate is configured for the category
}

// The flow capability + commodity a run belongs to — sourced from the active
// flow manifest (useFlowManifest). Force-cooling flows are the berry family;
// commodityName is commodities.name (e.g. 'BLUEBERRIES', 'STRAWBERRIES').
export interface FruitTypeContext {
  usesForceCooling?: boolean
  commodityName?:    string | null
}

// The citrus categories: ALWAYS priced (a configured 0 renders R 0 exactly as
// it always has). Anything else — every berry commodity — is priced only when a
// rate is explicitly configured; a citrus rand-value on a berry run is worse
// than showing none.
const CITRUS_CATEGORIES = new Set(['Lemons', 'Oranges', 'Soft Citrus', 'Tango'])

// 'BLUEBERRIES' → 'Blueberries', 'STRAWBERRIES' → 'Strawberries'. A force-cooling
// flow with no commodity name falls back to a generic (unpriced) 'Berries'.
function berryCategory(commodityName?: string | null): string {
  const n = (commodityName ?? '').trim()
  if (!n) return 'Berries'
  return n.charAt(0).toUpperCase() + n.slice(1).toLowerCase()
}

// Mirror of the SQL helper in migration 040 for the CITRUS variety map — kept
// identical so client + server classify citrus varieties the same way. Berry
// flows classify by commodity (usesForceCooling), never the citrus variety map:
// a strawberry variety would otherwise fall through to 'Soft Citrus' and be
// priced at the citrus rate (the VENTURA/R15/kg bug this guards against).
export function fruitTypeFor(variety: string | null, ctx?: FruitTypeContext): string {
  if (ctx?.usesForceCooling) return berryCategory(ctx.commodityName)
  if (!variety) return 'Soft Citrus'
  const v = variety.toLowerCase()
  if (v.includes('lemon'))  return 'Lemons'
  if (v.includes('orange')) return 'Oranges'
  if (v.includes('tango'))  return 'Tango'
  return 'Soft Citrus'
}

const CONFIG_KEY: Record<string, string> = {
  Lemons:        'shrinkage_value_per_kg_lemons',
  Oranges:       'shrinkage_value_per_kg_oranges',
  'Soft Citrus': 'shrinkage_value_per_kg_soft_citrus',
  Tango:         'shrinkage_value_per_kg_tango',
  Blueberries:   'shrinkage_value_per_kg_blueberries',
  Strawberries:  'shrinkage_value_per_kg_strawberries',
}

// Citrus categories default to R15/kg when unconfigured (long-standing
// behaviour). Berry categories deliberately default to 0 = UNPRICED.
const DEFAULT_RATE: Record<string, number> = {
  Lemons: 15, Oranges: 15, 'Soft Citrus': 15, Tango: 15,
  Blueberries: 0, Strawberries: 0,
}

export function computeShrinkage(run: RunMass, ratesByFruitType: Record<string, number>, ctx?: FruitTypeContext): ShrinkageBreakdown {
  const variance = (run.tipped_net_weight_kg ?? 0)
                 - (run.packed_weight_kg     ?? 0)
                 - (run.reject_weight_kg     ?? 0)
                 - (run.waste_weight_kg      ?? 0)
  const shrinkage = Math.max(0, variance)
  const fruit = fruitTypeFor(run.variety, ctx)
  const rate = ratesByFruitType[fruit] ?? 0
  return {
    variance_kg:  variance,
    shrinkage_kg: shrinkage,
    fruit_type:   fruit,
    rate,
    value_zar:    shrinkage * rate,
    // Citrus categories are always priced; any berry category is priced only
    // when a rate is configured (rate > 0).
    priced:       CITRUS_CATEGORIES.has(fruit) ? true : rate > 0,
  }
}

// Hook — fetches the per-tenant config rows once and keeps them in cache for
// an hour. Tenant-scoped BOTH ways: the key (so a tenant switch can't restore
// the previous tenant's rates from the persisted cache) and the read (a
// platform admin's RLS returns every tenant's rows — mig 696 class).
export function useShrinkageRates() {
  const { data: tenant } = useActiveTenant()
  const tenantId = tenant?.tenantId ?? null
  return useQuery({
    queryKey: ['shrinkage-rates', tenantId],
    staleTime: 60 * 60 * 1_000,
    enabled: tenantId !== null,
    queryFn: async () => {
      const { data, error } = await supabase.from('system_config')
        .select('config_key, config_value')
        .eq('tenant_id', tenantId)
        .in('config_key', Object.values(CONFIG_KEY))
      if (error) throw error
      const rates: Record<string, number> = {}
      for (const r of data ?? []) {
        for (const [fruit, key] of Object.entries(CONFIG_KEY)) {
          if (r.config_key === key) rates[fruit] = parseFloat(r.config_value) || 0
        }
      }
      // Sane fallbacks if a key is missing (Blueberries: 0 = unpriced).
      for (const fruit of Object.keys(CONFIG_KEY)) {
        if (rates[fruit] == null) rates[fruit] = DEFAULT_RATE[fruit] ?? 15
      }
      return rates
    },
  })
}

// Tailwind colour helper — turns a value into a useful colour bucket.
//   0–500   → green (operational noise)
//   500–2000 → amber (worth investigating)
//   2000+   → red (something is wrong)
export function shrinkageColour(value_zar: number): string {
  if (value_zar < 500)  return 'text-green-400'
  if (value_zar < 2000) return 'text-amber-400'
  return 'text-red-400'
}
